<template>
  <div class="task-row" :class="{ 'task-rejected': hasITORejection }">

    <!-- Fila superior: info + acciones -->
    <div class="task-top-row">
      <div class="task-info">
        <p class="task-name">{{ task.punto_control }}</p>
        <p class="task-sub">{{ task.subpartida }}</p>
      </div>
      <div class="task-actions">
        <v-tooltip bottom v-if="hasApprovals">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="#2D4B95" icon small v-bind="attrs" v-on="on" @click.stop="$emit('history', task.id)">
              <v-icon>mdi-history</v-icon>
            </v-btn>
          </template>
          <span>Ver historial</span>
        </v-tooltip>
        <v-tooltip bottom v-if="isITO && !autocontrol && meetsMinimum">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="#fb8c00" icon small v-bind="attrs" v-on="on" :loading="loadingAutocontrol" @click.stop="$emit('autocontrol', task)">
              <v-icon>mdi-shield-account</v-icon>
            </v-btn>
          </template>
          <span>Permitir Autocontrol</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Fila inferior: badges de aprobación -->
    <div class="task-badges">
      <v-tooltip top v-for="(subject, index) in jobTypes" :key="index" max-width="300" color="white">
        <template v-slot:activator="{ on, attrs }">
          <div class="badge-group" v-bind="attrs" v-on="on">
            <!-- Fila de iconos -->
            <div class="badge-icons-row">
              <!-- Badge principal: vacío si el usuario aún puede aprobar esta tarea -->
              <v-btn
                class="badge-btn"
                plain
                :disabled="status >= 3"
                @click.stop="handleApprovalClick(subject, task.id)"
              >
                <div class="d-flex flex-column justify-center align-center text-center">
                  <div class="badge-icon-wrapper">
                    <template v-if="canUserApproveTaskSubject(task.id, subject.id)">
                      <v-icon color="#9CA3AF" size="20">mdi-circle-outline</v-icon>
                    </template>
                    <template v-else>
                      <v-icon :color="getBadgeColor(task.id, subject.id)" size="20">
                        {{ getBadgeIcon(task.id, subject.id) }}
                      </v-icon>
                      <span v-if="isBadgePartial(task.id, subject.id)" class="badge-partial-dot" />
                    </template>
                  </div>
                  <div class="badge-label">{{ subject.name }}</div>
                </div>
              </v-btn>

              <!-- Badge adicional: otros usuarios con el mismo perfil que ya aprobaron -->
              <div
                v-if="canUserApproveTaskSubject(task.id, subject.id) && countOtherApproversForTask(task.id, subject.id) > 0"
                class="badge-others-wrapper"
              >
                <v-btn
                  class="badge-btn"
                  plain
                  :disabled="status >= 3"
                  @click.stop="handleApprovalClick(subject, task.id)"
                >
                  <div class="d-flex flex-column justify-center align-center text-center">
                    <div class="badge-icon-wrapper">
                      <v-icon :color="getBadgeColor(task.id, subject.id)" size="20">
                        {{ getBadgeIcon(task.id, subject.id) }}
                      </v-icon>
                      <div v-if="countOtherApproversForTask(task.id, subject.id) > 1" class="badge-side-count">
                        x{{ countOtherApproversForTask(task.id, subject.id) }}
                      </div>
                    </div>
                    <div class="badge-label">{{ subject.name }}</div>
                  </div>
                </v-btn>
              </div>
            </div>

            <!-- Contador centrado bajo ambos badges -->
            <div class="badge-counter-centered">
              {{ getApprovedCount(task.id, subject.id) }}/{{ getApprovalConfig(subject.id).max || 1 }}
            </div>
          </div>
        </template>

        <!-- Tooltip enriquecido -->
        <div class="tr-tooltip-content">
        <div class="tr-tooltip-header" style="color: #2D4B95;">
            <strong>{{ subject.name }}</strong>
            <span class="tr-tooltip-counter">
              {{ getApprovedCount(task.id, subject.id) }}/{{ getApprovalConfig(subject.id).max || 1 }} aprobaciones posibles
            </span>
          </div>

          <template v-if="getApprovalList(task.id, subject.id).length">
            <div
              class="tr-tooltip-row"
              v-for="(aprov, i) in getApprovalList(task.id, subject.id)"
              :key="i"
            >
              <div class="tr-tooltip-task">{{ aprov.user?.name || 'Usuario' }}</div>
              <div class="tr-tooltip-meta">
                <span :style="{ color: getTooltipEstadoColor(aprov.estado), fontWeight: '600' }">
                  {{ aprov.estado }}
                </span>
                <span v-if="aprov.comentario" class="tr-tooltip-comment">
                  — &ldquo;{{ aprov.comentario }}&rdquo;
                </span>
              </div>
            </div>
          </template>
          <div v-else class="tr-tooltip-empty">Sin aprobaciones registradas</div>
        </div>
      </v-tooltip>

      <!-- Badge ITO -->
      <v-tooltip top v-if="(!autocontrol && meetsMinimum) || hasGlobalITORejection">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="badge-btn"
            plain
            :disabled="status >= 3"
            v-bind="attrs"
            v-on="on"
            @click="handleApprovalClick(null, task.id)"
          >
            <div class="d-flex flex-column justify-center align-center text-center">
              <v-icon :color="getBadgeColor(task.id, null)" size="20">
                {{ getBadgeIcon(task.id, null) }}
              </v-icon>
              <div class="badge-label">ITO</div>
            </div>
          </v-btn>
        </template>
        <div class="tr-tooltip-content">
          <div class="tr-tooltip-header" style="color: #2D4B95;">
            <strong>ITO</strong>
            <span class="tr-tooltip-counter">{{ getApprovedCount(task.id, null) }}/{{ getApprovalConfig(null).max || 1 }} aprobaciones posibles</span>
          </div>
          <template v-if="getApprovalList(task.id, null).length">
            <div
              class="tr-tooltip-row"
              v-for="(aprov, i) in getApprovalList(task.id, null)"
              :key="i"
            >
              <div class="tr-tooltip-task">{{ aprov.approver_name || aprov.user_name || 'ITO' }}</div>
              <div class="tr-tooltip-meta">
                <span :style="{ color: getTooltipEstadoColor(aprov.estado), fontWeight: '600' }">
                  {{ aprov.estado }}
                </span>
                <span v-if="aprov.comentario" class="tr-tooltip-comment">
                  — &ldquo;{{ aprov.comentario }}&rdquo;
                </span>
              </div>
            </div>
          </template>
          <div v-else class="tr-tooltip-empty">Sin aprobaciones registradas</div>
        </div>
      </v-tooltip>
    </div>

  </div>
</template>

<script>
export default {
  name: 'TaskRow',

  props: {
    config: {
      type: Object,
      required: true
    },
    entrega: {
      type: Object,
      required: true
    },
    task: {
      type: Object,
      required: true
    },
    hasGlobalITORejection: {
      type: Boolean,
      default: false
    },
    jobTypes: {
      type: Array,
      required: true
    },
    isITO: {
      type: Boolean,
      default: false
    },
    autocontrol: {
      type: Boolean,
      default: false
    },
    loadingAutocontrol: {
      type: Boolean,
      default: false
    },
    isApproving: {
      type: Boolean,
      default: false
    },
    meetsMinimum: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    hasApprovals() {
      return this.task.approvals && this.task.approvals.length > 0;
    },

    hasITORejection() {
      return this.task.rechazoITODatetime;
    },

    status() {
      return this.entrega.status;
    },

    isDisabled() {
      return this.entrega.status >= 3;
    },

    currentUser() {
      return this.$store.userStore?.state?.userInfo?.info || {};
    }
  },

  methods: {
    /**
     * Retorna la config de aprobadores para un subject_id dado.
     * Fallback: min=1, max=1 si no está definido.
     */
    getApprovalConfig(subjectId) {
      if (!this.config?.aprobadores) return { min: 1, max: 1 };
      // Normalizar subjectId nulo (ITO) a id=4 en la config
      const lookupId = subjectId === null ? 4 : subjectId;
      return this.config.aprobadores.find(a => a.id === lookupId) || { min: 1, max: 1 };
    },

    /**
     * Retorna TODAS las aprobaciones para (taskId, subjectId),
     * ordenadas de más reciente a más antigua.
     * Para ITO: subjectId === null → subject_id === null en approval.
     */
    getApprovalList(taskId, subjectId) {
      if (!this.task.approvals) return [];
      return [...this.task.approvals]
        .filter(a =>
          a.task_id === taskId &&
          (subjectId === null
            ? a.subject_id === null
            : a.subject_id === subjectId)
        )
        .sort((a, b) => b.id - a.id);
    },

    /**
     * Cuenta cuántas aprobaciones distintas (por `user_id`) tienen estado 'Aprobado'
     * o 'Aprobado con Observaciones'. Retorna cantidad de usuarios únicos.
     */
    getApprovedCount(taskId, subjectId) {
      const list = this.getApprovalList(taskId, subjectId).filter(a =>
        a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones'
      );
      const users = new Set(list.map(a => (a.user_id !== undefined ? a.user_id : (a.user?.id || null))));
      users.delete(null);
      return users.size;
    },

    /**
     * ¿Hay al menos un rechazo activo?
     */
    hasRejection(taskId, subjectId) {
      return this.getApprovalList(taskId, subjectId).some(a => a.estado === 'Rechazado');
    },

    /**
     * ¿El mínimo está cumplido?
     */
    meetsMinCount(taskId, subjectId) {
      const { min } = this.getApprovalConfig(subjectId);
      return this.getApprovedCount(taskId, subjectId) >= (min || 1);
    },

    /**
     * ¿Hay aprobaciones pero aún no se cumple el mínimo? (estado parcial)
     */
    isBadgePartial(taskId, subjectId) {
      const count = this.getApprovedCount(taskId, subjectId);
      const { min } = this.getApprovalConfig(subjectId);
      return count > 0 && count < (min || 1);
    },

    getBadgeColor(taskId, subjectId) {
      const list = this.getApprovalList(taskId, subjectId);

      if (!list.length) {
        if (this.autocontrol && subjectId !== null) return '#fb8c00';
        return '#9CA3AF';
      }

      // Si hay algún rechazo, rojo
      if (this.hasRejection(taskId, subjectId)) return '#F4516C';

      // Si cumple mínimo aprobado → verde
      if (this.meetsMinCount(taskId, subjectId)) {
        // Si todos son 'Aprobado con Observaciones' → azul
        const allWithObs = list.every(a => a.estado === 'Aprobado con Observaciones');
        if (allWithObs) return '#5E85EF';
        // Diferenciar si ya se alcanzó el máximo configurado
        const approvedCount = this.getApprovedCount(taskId, subjectId);
        const { max } = this.getApprovalConfig(subjectId);
        if (approvedCount >= (max || 1)) return '#2e7d32'; // verde oscuro: full
        return '#3EBFA3'; // verde claro: aún puede crecer
      }

      // Tiene aprobaciones pero no cumple mínimo → amarillo parcial
      if (this.getApprovedCount(taskId, subjectId) > 0) return '#FFC107';

      // Solo observaciones
      if (list.some(a => a.estado === 'Observado')) return '#FFC107';

      return '#9CA3AF';
    },

    getBadgeIcon(taskId, subjectId) {
      const list = this.getApprovalList(taskId, subjectId);

      if (!list.length) {
        if (this.autocontrol && subjectId !== null) return 'mdi-shield-check-outline';
        return 'mdi-circle-outline';
      }

      if (this.hasRejection(taskId, subjectId)) return 'mdi-close-circle-outline';

      if (this.meetsMinCount(taskId, subjectId)) return 'mdi-check-circle-outline';

      // Parcial: hay aprobados pero no llega al mínimo
      if (this.getApprovedCount(taskId, subjectId) > 0) return 'mdi-circle-half-full';

      if (list.some(a => a.estado === 'Observado')) return 'mdi-circle-half-full';

      return 'mdi-circle-outline';
    },

    getApprovalStatusText(taskId, subjectId) {
      const list = this.getApprovalList(taskId, subjectId);
      if (!list.length) {
        if (this.autocontrol && subjectId !== null) return 'Autocontrol Habilitado';
        return 'Pendiente';
      }
      if (this.hasRejection(taskId, subjectId)) return 'Rechazado';
      if (this.meetsMinCount(taskId, subjectId)) return 'Aprobado';
      return `Parcial (${this.getApprovedCount(taskId, subjectId)}/${this.getApprovalConfig(subjectId).max || 1})`;
    },

    getInitials(name) {
      return name.split(/\s/).reduce((r, w) => r += w.slice(0, 1), '');
    },

    isCurrentUserJob(subjectId) {
      if (subjectId === null) return this.isITO;
      return this.$store.projectsStore.state.selectedProject?.job_type?.id === subjectId;
    },

    getTooltipEstadoColor(estado) {
      const colors = {
        'Aprobado': '#2e7d32',
        'Observado': '#e65100',
        'Rechazado': '#c62828',
        'Aprobado con Observaciones': '#2D4B95',
      };
      return colors[estado] || '#9CA3AF';
    },

    handleApprovalClick(subject, taskId) {
      const subjectId = subject ? subject.id : null;
      const canApprove = this.checkPermissions(['interact_entregas']) &&
        (this.isCurrentUserJob(subjectId) || (this.autocontrol && subjectId !== null));

      if (!canApprove || this.status >= 3) {
        let data = {
          message: 'No tienes permisos para aprobar esta tarea.',
          title: 'Permisos insuficientes',
          created: new Date(),
          type: 'error',
          icon: 'mdi-alert-circle',
          color: '#F44336'
        };
        if (this.checkPermissions(['interact_entregas']) && this.status >= 3) {
          data.message = 'No se pueden aprobar tareas de una entrega finalizada.';
          data.title = 'Entrega finalizada';
        } else if (!this.isCurrentUserJob(subjectId)) {
          data.message = 'No tienes este rol en el proyecto actual.';
          data.title = 'Rol incorrecto';
        }
        this.$store.globalStore.dispatch('addMessage', data);
        return;
      }

      // Validación frontend: no intentar aprobar si ya se alcanzó el máximo
      const canApproveMore = this.canSubjectAcceptMoreApprovals(taskId, subjectId);
      if (!canApproveMore) {
        const approved = this.getApprovedCount(taskId, subjectId);
        const { max } = this.getApprovalConfig(subjectId);
        this.$store.globalStore.dispatch('addMessage', {
          message: `Máximo de aprobaciones alcanzado (${approved}/${max || 1})`,
          title: 'Máximo alcanzado',
          created: new Date(),
          type: 'error',
          icon: 'mdi-block-helper',
          color: '#F44336'
        });
        return;
      }

      // Para el emit se sigue pasando la aprobación más reciente como contexto
      const list = this.getApprovalList(taskId, subjectId);
      const latest = list[0] || null;

      this.$emit('approval', {
        taskId,
        subject,
        subjectId: subjectId === null ? 4 : subjectId,
        taskName: this.task.punto_control,
        attachment: latest ? latest.attachment : null,
        estado: latest ? latest.estado : 'Aprobado',
        comentario: latest ? latest.comentario || '' : '',
        isExisting: !!latest,
        approvedCount: this.getApprovedCount(taskId, subjectId),
        minRequired: this.getApprovalConfig(subjectId).min,
        maxAllowed: this.getApprovalConfig(subjectId).max
      });
    },

    /**
     * Devuelve true si el usuario actual puede aprobar esta tarea para el subject dado:
     * - Tiene el mismo job que el subject
     * - No ha aprobado aún esta tarea
     * - No se ha alcanzado el máximo de aprobaciones
     */
    canUserApproveTaskSubject(taskId, subjectId) {
      if (!this.isCurrentUserJob(subjectId)) return false;
      const userId = this.currentUser?.id || null;
      if (!userId) return false;
      const approvedList = this.getApprovalList(taskId, subjectId).filter(a =>
        a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones'
      );
      const userAlready = approvedList.some(a =>
        (a.user_id !== undefined ? a.user_id : (a.user?.id || null)) === userId
      );
      if (userAlready) return false;
      const { max } = this.getApprovalConfig(subjectId);
      return this.getApprovedCount(taskId, subjectId) < (max || 1);
    },

    /**
     * Cuenta usuarios únicos (EXCLUYENDO al usuario actual) que han aprobado
     * esta tarea para el subject dado.
     */
    countOtherApproversForTask(taskId, subjectId) {
      const userId = this.currentUser?.id || null;
      const list = this.getApprovalList(taskId, subjectId).filter(a =>
        a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones'
      );
      const ids = new Set(
        list
          .map(a => (a.user_id !== undefined ? a.user_id : (a.user?.id || null)))
          .filter(id => id !== null && id !== userId)
      );
      return ids.size;
    },

    /**
     * Devuelve true si el subject de la tarea aún admite aprobaciones adicionales
     * según el `max` configurado. Si el máximo ya fue alcanzado, permite editar
     * la aprobación sólo si el usuario actual ya ha aprobado (para permitir editar).
     * Autocontrol no depende de `max`.
     */
    canSubjectAcceptMoreApprovals(taskId, subjectId) {
      // Autocontrol no está sujeto a límites
      if (this.task && this.task.autocontrol) return true;

      const approvedCount = this.getApprovedCount(taskId, subjectId);
      const { max } = this.getApprovalConfig(subjectId);
      const limit = max || 1;

      if (approvedCount < limit) return true;

      // Si llegó al límite: permitir acción sólo si el usuario ya figura entre los aprobadores
      const list = this.getApprovalList(taskId, subjectId).filter(a =>
        a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones'
      );
      const userId = this.currentUser?.id || this.currentUser?.user_id || null;
      if (!userId) return false;
      return list.some(a => (a.user_id !== undefined ? a.user_id : (a.user?.id || null)) === userId);
    }
  }
};
</script>

<style scoped>
/* ── ROW ── */
.task-row {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  border-bottom: 1px solid #dedede;
  transition: background-color 0.15s;
}
.task-row:hover { background-color: #F8F9FC; }
.task-rejected {
  background-color: #FFF0F0 !important;
  border-left: 3px solid #F4516C;
}
.task-rejected:hover { background-color: #FFE0E0 !important; }

/* ── TOP ROW ── */
.task-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.task-info { flex: 1; min-width: 0; }
.task-name {
  font-size: 12px;
  font-weight: 600;
  color: #1A1D23;
  margin: 0 0 1px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.task-sub {
  font-size: 11px;
  color: #6B7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.task-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

/* ── BADGES ── */
.task-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  margin-top: 4px;
}
.badge-group {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}
.badge-icons-row {
  display: flex;
  align-items: flex-start;
}
.badge-counter-centered {
  font-size: 10px;
  color: #6B7280;
  text-align: center;
  margin-top: 1px;
  line-height: 1;
  width: 100%;
}
.badge-others-wrapper {
  position: relative;
}
.badge-side-count {
  position: absolute;
  top: 2px;
  left: 65%;
  transform: translateX(-50%);
  z-index: 4;
  background: #374151;
  color: #fff;
  border-radius: 6px;
  padding: 0 4px;
  font-size: 9px;
  line-height: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.badge-btn,
.badge-btn:disabled {
  height: auto !important;
  min-height: unset !important;
  min-width: unset !important;
  padding: 4px 8px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: transparent !important;
  box-shadow: none !important;
  border: none;
}
.badge-btn.v-btn--plain:not(.v-btn--active):not(.v-btn--loading):not(:focus):not(:hover) .v-btn__content {
  opacity: 0.8;
}

/* Wrapper para el punto de progreso parcial */
.badge-icon-wrapper {
  position: relative;
  display: inline-flex;
}
.badge-partial-dot {
  position: absolute;
  top: -2px;
  right: -3px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #FFC107;
  border: 1px solid #fff;
}

.badge-label {
  font-size: 9px;
  color: grey;
  margin-top: 1px;
  line-height: 1.1;
  text-align: center;
  max-width: 56px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Tooltip ── */
.tr-tooltip-content { min-width: 180px; }
.tr-tooltip-header {
  font-size: 12px;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #E0E7FF;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.tr-tooltip-counter {
  font-size: 10px;
  color: #6B7280;
  font-weight: 400;
  white-space: nowrap;
}
.tr-tooltip-row {
  padding: 4px 0;
  border-top: 1px solid #F3F4F6;
}
.tr-tooltip-task {
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  white-space: normal;
  line-height: 1.3;
}
.tr-tooltip-meta {
  font-size: 11px;
  line-height: 1.3;
}
.tr-tooltip-comment {
  font-style: italic;
  color: #6B7280;
  white-space: normal;
  word-break: break-word;
}
.tr-tooltip-empty {
  font-size: 11px;
  color: #9CA3AF;
  font-style: italic;
}
</style>